import React, { useId, useState } from 'react';
import './ClinicalNoteExpandableField.css';

/**
 * Accessible, controlled, expandable narrative field for clinical-note sections.
 * The parent owns data, validation and autosave. This component only changes UI.
 */
export default function ClinicalNoteExpandableField({
  label,
  value = '',
  onChange,
  disabled = false,
  readOnly = false,
  required = false,
  placeholder = '',
  rows = 3,
  maxLength,
  description,
  error,
  id,
  name,
  defaultExpanded = false,
  initiallyCollapsed = false,
  className = '',
}) {
  const generatedId = useId();
  const inputId = id || `clinical-expandable-${generatedId}`;
  const hintId = `${inputId}-hint`;
  const errorId = `${inputId}-error`;
  const [expanded, setExpanded] = useState(Boolean(defaultExpanded));
  const [collapsed, setCollapsed] = useState(Boolean(initiallyCollapsed));

  const hasError = Boolean(error);
  const text = value == null ? '' : String(value);
  const collapsedSummary = text.trim() || 'No comment entered';

  return (
    <section className={`clinical-expandable-field ${className}`.trim()}>
      <div className="clinical-expandable-field__heading">
        <label htmlFor={inputId} className="clinical-expandable-field__label">
          {label}{required && <span aria-hidden="true"> *</span>}
        </label>
        <div className="clinical-expandable-field__actions">
          <button
            type="button"
            className="clinical-expandable-field__action"
            onClick={() => setCollapsed((previous) => !previous)}
            aria-expanded={!collapsed}
            aria-controls={`${inputId}-body`}
          >
            {collapsed ? 'Show' : 'Minimize'}
          </button>
          {!collapsed && (
            <button
              type="button"
              className="clinical-expandable-field__action"
              onClick={() => setExpanded((previous) => !previous)}
              aria-pressed={expanded}
            >
              {expanded ? 'Normal size' : 'Expand'}
            </button>
          )}
        </div>
      </div>
      <div id={`${inputId}-body`}>
        {collapsed ? (
          <button
            type="button"
            className="clinical-expandable-field__summary"
            onClick={() => setCollapsed(false)}
            title="Open this narrative field"
          >
            {collapsedSummary}
          </button>
        ) : (
          <>
            <textarea
              id={inputId}
              name={name}
              className={`clinical-expandable-field__input${expanded ? ' is-expanded' : ''}${hasError ? ' has-error' : ''}`}
              value={text}
              onChange={(event) => onChange?.(event.target.value)}
              disabled={disabled}
              readOnly={readOnly}
              required={required}
              placeholder={placeholder}
              rows={Math.max(2, Number(rows) || 3)}
              maxLength={maxLength}
              aria-invalid={hasError || undefined}
              aria-describedby={[description ? hintId : null, hasError ? errorId : null].filter(Boolean).join(' ') || undefined}
            />
            {description && <div id={hintId} className="clinical-expandable-field__hint">{description}</div>}
            {hasError && <div id={errorId} className="clinical-expandable-field__error" role="alert">{error}</div>}
          </>
        )}
      </div>
    </section>
  );
}
